import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { Factory } from 'rosie';

import {
  ECoverageKey,
  IAutoCoverageDetail,
  IAutoCoverageValues,
} from '../../../interfaces';
import coverageKeys from '../../constants/coverageKeys';

import { getRegionConfig } from './helpers';

function buildCoverageDetails(
  details: Partial<IAutoCoverageDetail> &
    Pick<IAutoCoverageDetail, 'key_name' | 'title' | 'values'>
) {
  return (overrides?: Partial<Omit<IAutoCoverageDetail, 'key_name'>>) => ({
    combo_idx: null,
    default: null,
    help_text: null,
    notice: null,
    description: `a description of ${details.key_name}`,
    ...details,
    ...omit(overrides, ['key_name']),
  });
}

export const coverageDetailBuilders = {
  ADDITIONAL_EQUIPMENT: buildCoverageDetails({
    key_name: coverageKeys.ADDITIONAL_EQUIPMENT,
    title: 'Additional Equipment Coverage',
    values: [null, '1000', '5000'],
  }),
  BODILY_INJURY_LIMIT: buildCoverageDetails({
    key_name: coverageKeys.BODILY_INJURY_LIMIT,
    title: 'Bodily Injury',
    values: ['25000/50000', '50000/100000', '100000/300000', '250000/500000'],
  }),
  COLLISION_DEDUCTIBLE: buildCoverageDetails({
    key_name: coverageKeys.COLLISION_DEDUCTIBLE,
    title: 'Collision Deductible',
    values: [null, '100', '250', '500', '1000', '2000'],
  }),
  COLLISION_DEDUCTIBLE_WAIVER: buildCoverageDetails({
    key_name: coverageKeys.COLLISION_DEDUCTIBLE_WAIVER,
    title: 'Collision Deductible Waiver',
    values: [false, true],
  }),
  COMPREHENSIVE_DEDUCTIBLE: buildCoverageDetails({
    key_name: coverageKeys.COMPREHENSIVE_DEDUCTIBLE,
    title: 'Comprehensive Deductible',
    values: [null, '100', '250', '500', '1000', '2000'],
  }),
  GAP: buildCoverageDetails({
    key_name: coverageKeys.GAP,
    title: 'Gap Coverage',
    values: [false, true],
  }),
  GLASS: buildCoverageDetails({
    key_name: coverageKeys.GLASS,
    title: 'Glass & Safety Equipment - Deductible Waiver',
    values: [false, true],
  }),
  LOAN_LEASE_PAYOFF: buildCoverageDetails({
    key_name: coverageKeys.LOAN_LEASE_PAYOFF,
    title: 'Loan/Lease Payoff',
    values: [true, false],
  }),
  LOSS_OF_USE: buildCoverageDetails({
    key_name: coverageKeys.LOSS_OF_USE,
    title: 'Loss of Use',
    values: [null, '30', '50'],
  }),
  MEDICAL_LIMIT: buildCoverageDetails({
    key_name: coverageKeys.MEDICAL_LIMIT,
    title: 'Medical Payments',
    values: [null, '500', '1000', '2000', '5000', '10000'],
  }),
  NEW_CAR_REPLACEMENT: buildCoverageDetails({
    key_name: coverageKeys.NEW_CAR_REPLACEMENT,
    title: 'New Car Replacement',
    values: [true, false],
  }),
  OEM_PARTS: buildCoverageDetails({
    key_name: coverageKeys.OEM_PARTS,
    title: 'OEM Parts',
    values: [true, false],
  }),
  PERSONAL_INJURY_PROTECTION_LIMIT: buildCoverageDetails({
    key_name: coverageKeys.PERSONAL_INJURY_PROTECTION_LIMIT,
    title: 'Personal Injury Protection',
    values: [null, '25000', '50000', '100000'],
  }),
  PET_PASSENGER: buildCoverageDetails({
    key_name: coverageKeys.PET_PASSENGER,
    title: 'Pet Passenger',
    values: [false, true],
  }),
  PROPERTY_DAMAGE_LIMIT: buildCoverageDetails({
    key_name: coverageKeys.PROPERTY_DAMAGE_LIMIT,
    title: 'Property Damage',
    values: ['25000', '50000', '100000'],
  }),
  RENTAL_REIMBURSEMENT: buildCoverageDetails({
    key_name: coverageKeys.RENTAL_REIMBURSEMENT,
    title: 'Rental Reimbursement',
    values: [null, '30', '50'],
  }),
  RIDESHARING: buildCoverageDetails({
    key_name: coverageKeys.RIDESHARING,
    title: 'Rideshare',
    values: [true, false],
  }),
  ROADSIDE_ASSISTANCE: buildCoverageDetails({
    key_name: coverageKeys.ROADSIDE_ASSISTANCE,
    title: 'Roadside Assistance',
    values: [true],
  }),
  SIDE_HUSTLE: buildCoverageDetails({
    key_name: coverageKeys.SIDE_HUSTLE,
    title: 'Side Hustle - Auto',
    values: [false, true],
  }),
  TEMPORARY_RIDE: buildCoverageDetails({
    key_name: coverageKeys.TEMPORARY_RIDE,
    title: 'Temporary Ride Coverage',
    values: ['30', '50', null],
  }),
  TOWING_ROAD_SERVICES: buildCoverageDetails({
    key_name: coverageKeys.TOWING_ROAD_SERVICES,
    title: 'Towing and Road Services',
    values: [true, false],
  }),
  UNDERINSURED_MOTORIST_BODILY_INJURY_LIMIT: buildCoverageDetails({
    key_name: coverageKeys.UNDERINSURED_MOTORIST_BODILY_INJURY_LIMIT,
    title: 'Underinsured Motorist Bodily Injury',
    values: [
      null,
      '25000/50000',
      '50000/100000',
      '100000/300000',
      '250000/500000',
    ],
  }),
  UNDERINSURED_MOTORIST_PROPERTY_DAMAGE_LIMIT: buildCoverageDetails({
    key_name: coverageKeys.UNDERINSURED_MOTORIST_PROPERTY_DAMAGE_LIMIT,
    title: 'Underinsured Motorist Property Damage',
    values: [null, '25000', '50000', '100000'],
  }),
  UNINSURED_MOTORIST_BODILY_INJURY_LIMIT: buildCoverageDetails({
    key_name: coverageKeys.UNINSURED_MOTORIST_BODILY_INJURY_LIMIT,
    title: 'Uninsured Motorist Bodily Injury',
    values: ['25000/50000', '50000/100000', '100000/300000', '250000/500000'],
  }),
  UNINSURED_MOTORIST_PROPERTY_DAMAGE_LIMIT: buildCoverageDetails({
    key_name: coverageKeys.UNINSURED_MOTORIST_PROPERTY_DAMAGE_LIMIT,
    title: 'Uninsured Motorist Property Damage',
    values: ['25000', '50000', '100000'],
  }),
};

export const allCoverageDetails = Object.values(coverageDetailBuilders).reduce(
  (acc, detailBuilder) => {
    const detail = detailBuilder();
    return {
      ...acc,
      [detail.key_name]: detail,
    };
  },
  {}
) as Record<ECoverageKey, IAutoCoverageDetail>;

export const CoverageDetails = new Factory<IAutoCoverageValues>()
  .option('regionCode', null)
  .after((_, { regionCode } = {}) => {
    const coverages = getRegionConfig(regionCode)?.coverages || [];
    const relevantCoverageKeys = coverages.map((coverage) => coverage.key);
    return pick(allCoverageDetails, relevantCoverageKeys);
  });
